import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  region: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => boolean;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Predefined user accounts matching .env configuration
const PREDEFINED_USERS: Record<string, { password: string; user: User }> = {
  'admin@hirsto.com': {
    password: 'Admin@2025',
    user: {
      id: 'admin-001',
      name: 'System Administrator',
      email: 'admin@hirsto.com',
      role: 'Administrator',
      department: 'Administration',
      region: 'All Regions',
      permissions: ['all']
    }
  },
  'automation@hirsto.com': {
    password: 'Auto@2025',
    user: {
      id: 'auto-001',
      name: 'Ahmed Al-Mansouri',
      email: 'automation@hirsto.com',
      role: 'Automation Manager',
      department: 'Claims',
      region: 'Dubai',
      permissions: ['modify_stp_rules', 'view_efficiency', 'redirect_tasks', 'view_operational_efficiency']
    }
  },
  'fwa@hirsto.com': {
    password: 'FWA@2025',
    user: {
      id: 'fwa-001',
      name: 'Maram Al-Sayed',
      email: 'fwa@hirsto.com',
      role: 'FWA Fraud Auditor',
      department: 'FWA',
      region: 'All Regions',
      permissions: ['freeze_payments', 'start_investigation', 'modify_alerts', 'view_fraud_detection']
    }
  },
  'actuarial@hirsto.com': {
    password: 'Actuarial@2025',
    user: {
      id: 'actuarial-001',
      name: 'Dr. Sarah Al-Kaabi',
      email: 'actuarial@hirsto.com',
      role: 'Actuarial Analyst',
      department: 'Actuarial',
      region: 'All Regions',
      permissions: ['modify_pricing', 'export_data', 'view_analytics', 'view_proactive_risk']
    }
  },
  'claims@hirsto.com': {
    password: 'Claims@2025',
    user: {
      id: 'claims-001',
      name: 'Khalid Hassan',
      email: 'claims@hirsto.com',
      role: 'Claims Processor',
      department: 'Claims',
      region: 'Abu Dhabi',
      permissions: ['process_claims', 'view_alerts', 'view_operational_efficiency']
    }
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('hirsto_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('hirsto_user');
      }
    }
  }, []);

  // Load additional employees from localStorage
  const getStoredEmployees = (): Record<string, { password: string; user: User }> => {
    const storedEmployees = localStorage.getItem('hirsto_employees');
    if (storedEmployees) {
      try {
        return JSON.parse(storedEmployees);
      } catch (error) {
        console.error('Error parsing stored employees:', error);
        return {};
      }
    }
    return {};
  };

  const login = (email: string, password: string): boolean => {
    // Check predefined users first
    const predefinedAccount = PREDEFINED_USERS[email.toLowerCase()];
    if (predefinedAccount && predefinedAccount.password === password) {
      setUser(predefinedAccount.user);
      setIsAuthenticated(true);
      localStorage.setItem('hirsto_user', JSON.stringify(predefinedAccount.user));
      return true;
    }

    // Check stored employees
    const storedEmployees = getStoredEmployees();
    const storedAccount = storedEmployees[email.toLowerCase()];
    if (storedAccount && storedAccount.password === password) {
      setUser(storedAccount.user);
      setIsAuthenticated(true);
      localStorage.setItem('hirsto_user', JSON.stringify(storedAccount.user));
      return true;
    }

    return false;
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('hirsto_user');
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.permissions.includes('all')) return true;
    return user.permissions.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user) return false;
    if (user.permissions.includes('all')) return true;
    return permissions.some(permission => user.permissions.includes(permission));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        hasPermission,
        hasAnyPermission
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get role permissions
export const getRolePermissions = (roleName: string): string[] => {
  switch (roleName) {
    case 'Administrator':
      return ['all'];
    case 'Automation Manager':
      return ['modify_stp_rules', 'view_efficiency', 'redirect_tasks', 'view_operational_efficiency'];
    case 'FWA Fraud Auditor':
      return ['freeze_payments', 'start_investigation', 'modify_alerts', 'view_fraud_detection'];
    case 'Actuarial Analyst':
      return ['modify_pricing', 'export_data', 'view_analytics', 'view_proactive_risk'];
    case 'Claims Processor':
      return ['process_claims', 'view_alerts', 'view_operational_efficiency'];
    default:
      return [];
  }
};
